/**
 * ✅ FERP CHAOS DOCUMENT UPLOADER
 *
 * Sprint 8 LLM A Track - Day 1-2
 *
 * Life-Critical Document Upload Component
 * - Drag-and-drop file upload with react-dropzone
 * - Multi-file support with progress tracking
 * - Real-time upload status visualization
 * - S3 presigned URL flow (HIPAA compliance)
 * - Domain detection and complexity analysis
 * - Error handling with retry logic
 *
 * ARCHITECTURE:
 * - Mantine UI only (no MUI components)
 * - Data-driven (all values from backend via hook)
 * - Backend ID generation only
 * - Multi-tenant isolation
 * - Immutable audit trail integration
 *
 * USAGE:
 * ```tsx
 * <ChaosDocumentUploader
 *   onUploadComplete={(results) => console.log('Uploaded:', results)}
 *   sourceSystem="external-integration"
 * />
 * ```
 */

import React, { useCallback, useState } from 'react';
import {
  Card,
  Stack,
  Group,
  Text,
  Badge,
  Progress,
  Button,
  Alert,
  Table,
  ActionIcon,
  Tooltip,
  Center,
  Box,
  Title,
  ThemeIcon,
} from '@mantine/core';
import {
  IconUpload,
  IconFile,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconRefresh,
  IconClock,
  IconFileUpload,
  IconCloudUpload,
} from '@tabler/icons-react';
import { useDropzone } from 'react-dropzone';
import { useChaosDocumentUpload, type UploadResult, type UploadProgress } from './hooks/useChaosDocumentUpload';
import { notifications } from '@mantine/notifications';

// Upload status constants (matches UploadProgress.status type)
const UploadStatus = {
  REQUESTING_URL: 'REQUESTING_URL',
  UPLOADING_TO_S3: 'UPLOADING_TO_S3',
  FINALIZING: 'FINALIZING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
} as const;

// ============================================================
// Component Props
// ============================================================

export interface ChaosDocumentUploaderProps {
  /**
   * Callback when upload completes successfully
   */
  onUploadComplete?: (results: UploadResult[]) => void;

  /**
   * Callback when upload fails
   */
  onUploadError?: (error: Error) => void;

  /**
   * Source system identifier (for audit trail)
   */
  sourceSystem?: string;

  /**
   * Additional metadata to attach to uploaded documents
   */
  metadata?: Record<string, any>;

  /**
   * Maximum number of files allowed (default: 10)
   */
  maxFiles?: number;

  /**
   * Maximum file size in bytes (default: 50MB)
   */
  maxFileSize?: number;

  /**
   * Accepted file types (default: PDF, DOCX, TXT, CSV, JSON)
   */
  acceptedFileTypes?: Record<string, string[]>;

  /**
   * Show compact view (hides details)
   */
  compact?: boolean;

  /**
   * Disable uploader
   */
  disabled?: boolean;
}

// ============================================================
// Default Values
// ============================================================

const DEFAULT_MAX_FILES = 10;
const DEFAULT_MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const DEFAULT_ACCEPTED_TYPES = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/msword': ['.doc'],
  'text/plain': ['.txt'],
  'text/csv': ['.csv'],
  'application/json': ['.json'],
  'application/xml': ['.xml'],
  'text/markdown': ['.md'],
};

// ============================================================
// Main Component
// ============================================================

export const ChaosDocumentUploader: React.FC<ChaosDocumentUploaderProps> = ({
  onUploadComplete,
  onUploadError,
  sourceSystem,
  metadata,
  maxFiles = DEFAULT_MAX_FILES,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  acceptedFileTypes = DEFAULT_ACCEPTED_TYPES,
  compact = false,
  disabled = false,
}) => {
  const {
    uploadProgress,
    completedUploads,
    isUploading,
    error,
    upload,
    uploadMultiple,
    retryUpload,
    cancelUpload,
    clearCompleted,
    totalUploads,
    completedCount,
    failedCount,
  } = useChaosDocumentUpload();

  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  /**
   * Handle file drop
   */
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (disabled) return;

      // Validate file count
      if (acceptedFiles.length + totalUploads > maxFiles) {
        notifications.show({
          title: 'Too Many Files',
          message: `Maximum ${maxFiles} files allowed. Currently uploading: ${totalUploads}`,
          color: 'orange',
        });
        return;
      }

      // Validate file sizes
      const oversizedFiles = acceptedFiles.filter((file) => file.size > maxFileSize);
      if (oversizedFiles.length > 0) {
        notifications.show({
          title: 'Files Too Large',
          message: `${oversizedFiles.length} file(s) exceed ${formatFileSize(maxFileSize)} limit`,
          color: 'orange',
        });
        return;
      }

      setPendingFiles(acceptedFiles);

      try {
        // Upload all files in parallel
        const results = await uploadMultiple(acceptedFiles, sourceSystem, metadata);

        // Notify parent component
        if (onUploadComplete) {
          onUploadComplete(results);
        }

        setPendingFiles([]);
      } catch (err) {
        const uploadError = err instanceof Error ? err : new Error('Upload failed');
        if (onUploadError) {
          onUploadError(uploadError);
        }
        setPendingFiles([]);
      }
    },
    [
      disabled,
      totalUploads,
      maxFiles,
      maxFileSize,
      uploadMultiple,
      sourceSystem,
      metadata,
      onUploadComplete,
      onUploadError,
    ]
  );

  /**
   * Handle retry for failed upload
   */
  const handleRetry = useCallback(
    async (progress: UploadProgress) => {
      const file = pendingFiles.find((f) => f.name === progress.filename);
      if (!file) {
        notifications.show({
          title: 'Retry Failed',
          message: 'Original file not found',
          color: 'red',
        });
        return;
      }

      try {
        await retryUpload(progress.documentId, file);
      } catch (err) {
        console.error('Retry failed:', err);
      }
    },
    [pendingFiles, retryUpload]
  );

  /**
   * react-dropzone configuration
   */
  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
    maxFiles,
    maxSize: maxFileSize,
    disabled: disabled || isUploading,
  });

  /**
   * Show file rejection errors
   */
  React.useEffect(() => {
    if (fileRejections.length > 0) {
      const errors = fileRejections.map((rejection) => ({
        file: rejection.file.name,
        errors: rejection.errors.map((e) => e.message),
      }));

      notifications.show({
        title: 'Files Rejected',
        message: `${errors.length} file(s) rejected. Check file type and size.`,
        color: 'red',
      });
    }
  }, [fileRejections]);

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        {/* Header */}
        <Group justify="apart">
          <Group gap="xs">
            <ThemeIcon size="lg" variant="light" color="blue">
              <IconCloudUpload size={20} />
            </ThemeIcon>
            <div>
              <Title order={4}>Chaos Document Upload</Title>
              <Text size="sm" c="dimmed">
                Upload unstructured documents for AI-powered template conversion
              </Text>
            </div>
          </Group>

          {completedCount > 0 && (
            <Button
              size="xs"
              variant="subtle"
              onClick={clearCompleted}
              leftSection={<IconCheck size={14} />}
            >
              Clear {completedCount} Completed
            </Button>
          )}
        </Group>

        {/* Upload Stats */}
        {!compact && totalUploads > 0 && (
          <Alert color="blue" icon={<IconFileUpload size={16} />}>
            <Group gap="xl">
              <div>
                <Text size="sm" fw={500}>
                  Total: {totalUploads}
                </Text>
              </div>
              <div>
                <Text size="sm" fw={500} c="green">
                  Completed: {completedCount}
                </Text>
              </div>
              {failedCount > 0 && (
                <div>
                  <Text size="sm" fw={500} c="red">
                    Failed: {failedCount}
                  </Text>
                </div>
              )}
            </Group>
          </Alert>
        )}

        {/* Global Error */}
        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} title="Upload Error">
            {error.message}
          </Alert>
        )}

        {/* Dropzone */}
        <Box
          {...getRootProps()}
          style={{
            border: '2px dashed',
            borderColor: isDragActive ? 'var(--mantine-color-blue-6)' : 'var(--mantine-color-gray-4)',
            borderRadius: '8px',
            padding: '2rem',
            backgroundColor: isDragActive
              ? 'var(--mantine-color-blue-0)'
              : disabled
                ? 'var(--mantine-color-gray-1)'
                : 'transparent',
            cursor: disabled || isUploading ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
          }}
        >
          <input {...getInputProps()} />
          <Center>
            <Stack ta="center" gap="xs">
              <ThemeIcon size={60} radius="xl" variant="light" color={isDragActive ? 'blue' : 'gray'}>
                <IconUpload size={30} />
              </ThemeIcon>
              <Text size="lg" fw={500}>
                {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
              </Text>
              <Text size="sm" c="dimmed">
                or click to browse ({formatFileSize(maxFileSize)} max, {maxFiles} files max)
              </Text>
              <Text size="xs" c="dimmed">
                Accepted: PDF, DOCX, TXT, CSV, JSON, XML, MD
              </Text>
            </Stack>
          </Center>
        </Box>

        {/* Upload Progress Table */}
        {uploadProgress.length > 0 && (
          <Card withBorder padding="sm">
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Upload Progress
              </Text>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>File</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Progress</Table.Th>
                    <Table.Th>Domain</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {uploadProgress.map((progress) => (
                    <UploadProgressRow
                      key={progress.documentId}
                      progress={progress}
                      onRetry={handleRetry}
                      onCancel={cancelUpload}
                      compact={compact}
                    />
                  ))}
                </Table.Tbody>
              </Table>
            </Stack>
          </Card>
        )}

        {/* Completed Uploads Summary */}
        {!compact && completedUploads.length > 0 && (
          <Card withBorder padding="sm">
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Completed Uploads ({completedUploads.length})
              </Text>
              <Table striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>File</Table.Th>
                    <Table.Th>Document ID</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Type</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {completedUploads.map((result) => (
                    <Table.Tr key={result.document.documentId}>
                      <Table.Td>
                        <Group gap="xs">
                          <IconFile size={16} />
                          <Text size="sm">{result.document.filename}</Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" ff="monospace">
                          {result.document.documentId}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color="green" variant="light">
                          {result.document.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge color="blue" variant="light">
                          {result.document.intakeTarget}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Stack>
          </Card>
        )}
      </Stack>
    </Card>
  );
};

// ============================================================
// Upload Progress Row Component
// ============================================================

interface UploadProgressRowProps {
  progress: UploadProgress;
  onRetry: (progress: UploadProgress) => void;
  onCancel: (documentId: string) => void;
  compact?: boolean;
}

const UploadProgressRow: React.FC<UploadProgressRowProps> = ({ progress, onRetry, onCancel, compact }) => {
  const statusColor = getStatusColor(progress.status);
  const statusIcon = getStatusIcon(progress.status);

  return (
    <Table.Tr>
      <Table.Td>
        <Group gap="xs">
          <IconFile size={16} />
          <Text size="sm" style={{ maxWidth: compact ? '150px' : '300px' }} truncate="end">
            {progress.filename}
          </Text>
        </Group>
      </Table.Td>
      <Table.Td>
        <Badge color={statusColor} variant="light" leftSection={statusIcon}>
          {formatStatus(progress.status)}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Box style={{ width: '150px' }}>
          <Progress
            value={progress.progress}
            size="sm"
            color={progress.status === UploadStatus.FAILED ? 'red' : progress.status === UploadStatus.COMPLETED ? 'green' : 'blue'}
            animated={progress.status === UploadStatus.UPLOADING_TO_S3}
          />
          <Text size="xs" c="dimmed">
            {Math.round(progress.progress)}% ({formatFileSize(progress.uploadedBytes)} / {formatFileSize(progress.totalBytes)})
          </Text>
        </Box>
      </Table.Td>
      <Table.Td>
        {progress.error ? (
          <Tooltip label={progress.error}>
            <Badge color="red" variant="light">
              Error
            </Badge>
          </Tooltip>
        ) : (
          <Text size="sm" c="dimmed">
            Detecting...
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          {progress.status === UploadStatus.FAILED && (
            <Tooltip label="Retry upload">
              <ActionIcon
                color="blue"
                variant="light"
                onClick={() => onRetry(progress)}
                size="sm"
              >
                <IconRefresh size={14} />
              </ActionIcon>
            </Tooltip>
          )}
          {progress.status !== UploadStatus.COMPLETED && progress.status !== UploadStatus.FAILED && (
            <Tooltip label="Cancel upload">
              <ActionIcon
                color="red"
                variant="light"
                onClick={() => onCancel(progress.documentId)}
                size="sm"
              >
                <IconX size={14} />
              </ActionIcon>
            </Tooltip>
          )}
          {progress.status === UploadStatus.COMPLETED && (
            <ThemeIcon color="green" variant="light" size="sm">
              <IconCheck size={14} />
            </ThemeIcon>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
};

// ============================================================
// Helper Functions
// ============================================================

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

function getStatusColor(status: UploadProgress['status']): string {
  switch (status) {
    case 'COMPLETED':
      return 'green';
    case 'FAILED':
      return 'red';
    case 'UPLOADING_TO_S3':
      return 'blue';
    case 'REQUESTING_URL':
    case 'FINALIZING':
      return 'yellow';
    default:
      return 'gray';
  }
}

function getStatusIcon(status: UploadProgress['status']): React.ReactNode {
  switch (status) {
    case 'COMPLETED':
      return <IconCheck size={12} />;
    case 'FAILED':
      return <IconX size={12} />;
    case 'UPLOADING_TO_S3':
      return <IconUpload size={12} />;
    default:
      return <IconClock size={12} />;
  }
}

function formatStatus(status: UploadProgress['status']): string {
  switch (status) {
    case 'REQUESTING_URL':
      return 'Preparing';
    case 'UPLOADING_TO_S3':
      return 'Uploading';
    case 'FINALIZING':
      return 'Finalizing';
    case 'COMPLETED':
      return 'Complete';
    case 'FAILED':
      return 'Failed';
    default:
      return 'Unknown';
  }
}

function getComplexityColor(complexity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'): string {
  switch (complexity) {
    case 'LOW':
      return 'green';
    case 'MEDIUM':
      return 'yellow';
    case 'HIGH':
      return 'orange';
    case 'CRITICAL':
      return 'red';
    default:
      return 'gray';
  }
}
